import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { MapPin, ArrowRight, CheckCircle2, Info, Star, Lock, X, Phone, FileText, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
import { QuoteModal } from './QuoteModal';
import { useT, useLocale } from '../utils/i18n';
import { SameDayBadge } from './SameDayBadge';
import { useCart } from './CartContext';
import { WelcomeBackCard, ReorderChip } from './WelcomeBackCard';
// Live ticker — rotates through recent activity. Uses an animated truck + sliding text instead of a blinking dot to avoid clashing with the green pulse in the trust badge above.
function LiveDeliveryTicker() {
  // These are ORDERS placed (web/phone), not delivery times. We constrain
  // the displayed window tightly to business hours so the feed always looks
  // like it came from the dispatch desk — no 9pm or 2am orders.
  // Active window: 6am – 6pm, with peak weighting toward 8am–5pm.
  // Sundays are quiet, so on Sunday most displayed orders pull from
  // Saturday and Friday.
  const ACTIVE_START_HOUR = 6; // 6:00 AM — earliest displayed order time
  const ACTIVE_END_HOUR = 18; // 6:00 PM — latest displayed order time
  const ACTIVE_MINUTES = (ACTIVE_END_HOUR - ACTIVE_START_HOUR) * 60; // 720
  // "Today progress" uses the dispatch-style 6am–5pm window for
  // weighting how many orders are from today vs prior days.
  const PROGRESS_END_HOUR = 17; // 5:00 PM
  const PROGRESS_MINUTES = (PROGRESS_END_HOUR - ACTIVE_START_HOUR) * 60; // 660
  // Live "now" — updates every 60s so on-screen timestamps tick forward
  // ("3 min ago" → "4 min ago") even if the user lingers on the page.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const formatRelative = (target: Date, now: Date) => {
    const diffMin = Math.max(1, Math.round((now.getTime() - target.getTime()) / 60000));
    const sameDay = target.toDateString() === now.toDateString();
    // Only show "X min ago" / "X hr ago" during business hours
    // (Mon–Sat 6am–6pm). Outside that window — including Sat 6pm
    // through Mon 6am — fall through to the dated time format so
    // the feed doesn't fake fresh minute-by-minute activity.
    const dow = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const hour = now.getHours();
    const inBusinessHours = dow >= 1 && dow <= 6 && hour >= 6 && hour < 18;
    if (inBusinessHours && sameDay && diffMin < 60) return `${diffMin} min ago`;
    if (inBusinessHours && sameDay && diffMin < 8 * 60) return `${Math.round(diffMin / 60)} hr ago`;
    const timeStr = target.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    if (sameDay) return `Today ${timeStr}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (target.toDateString() === yesterday.toDateString()) return `Yesterday ${timeStr}`;
    return `${target.toLocaleDateString('en-US', {
      weekday: 'short'
    })} ${timeStr}`;
  };
  // Static order info — only the timestamp varies based on the current time.
  // Distribution matches real sales: ~75% of orders are the four top sellers
  // (5/8" Minus, 3-Way Topsoil, Drain Rock, Bark). The remaining 25% is a
  // realistic mix of other materials.
  const baseActivities = [
  // 5/8" Minus — top seller (3 entries)
  {
    material: '5/8" Minus',
    qty: 13,
    city: 'Tacoma'
  }, {
    material: '5/8" Minus',
    qty: 10,
    city: 'Kent'
  }, {
    material: '5/8" Minus',
    qty: 14,
    city: 'Bellevue'
  },
  // 3-Way Topsoil — top seller (3 entries)
  {
    material: '3-Way Topsoil',
    qty: 7,
    city: 'Seattle'
  }, {
    material: '3-Way Topsoil',
    qty: 5,
    city: 'Puyallup'
  }, {
    material: '3-Way Topsoil',
    qty: 12,
    city: 'Auburn'
  },
  // Drain Rock — top seller (3 entries)
  {
    material: '7/8" Drain Rock',
    qty: 12,
    city: 'Federal Way'
  }, {
    material: '1.5" Drain Rock',
    qty: 10,
    city: 'Renton'
  }, {
    material: '7/8" Drain Rock',
    qty: 8,
    city: 'Tacoma'
  },
  // Bark — top seller (3 entries)
  {
    material: 'Medium Bark',
    qty: 14,
    city: 'Sammamish'
  }, {
    material: 'Coarse Bark',
    qty: 15,
    city: 'Lakewood'
  }, {
    material: 'Fine Bark',
    qty: 12,
    city: 'Kirkland'
  },
  // Other materials (~25%)
  {
    material: '1.25" Minus',
    qty: 12,
    city: 'Sumner'
  }, {
    material: 'Pea Gravel',
    qty: 6,
    city: 'Issaquah'
  }, {
    material: 'Recycled Concrete',
    qty: 9,
    city: 'Bonney Lake'
  }, {
    material: '5/8" Clear',
    qty: 8,
    city: 'Redmond'
  },
  // Occasional long-haul deliveries beyond Zone 5 — shows we go further
  {
    material: '5/8" Minus',
    qty: 14,
    city: 'Olympia'
  }, {
    material: '3-Way Topsoil',
    qty: 13,
    city: 'Everett'
  }];
  const activities = useMemo(() => {
    // Snapshot "now" once when the activities list is built. The activity
    // list itself is stable for this page session — only the rendered
    // relative-time strings update via the live `now` tick above.
    const built = new Date();
    const total = baseActivities.length;
    const dow = built.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const isSunday = dow === 0;
    // Per-page-load shuffle of material/qty across city slots. Different
    // every refresh so two visits in a row don't show the exact same feed.
    // Cities stay in their slot positions so geographic distribution is
    // preserved — only the material/qty pairing rotates.
    const shuffledIdx = baseActivities.map((_, i) => i);
    for (let i = shuffledIdx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledIdx[i], shuffledIdx[j]] = [shuffledIdx[j], shuffledIdx[i]];
    }
    const shuffledActivities = baseActivities.map((entry, i) => ({
      material: baseActivities[shuffledIdx[i]].material,
      qty: baseActivities[shuffledIdx[i]].qty,
      city: entry.city
    }));
    // Today's active window [5am → 10pm]
    const todayStart = new Date(built);
    todayStart.setHours(ACTIVE_START_HOUR, 0, 0, 0);
    const progressEnd = new Date(built);
    progressEnd.setHours(PROGRESS_END_HOUR, 0, 0, 0);
    // How far into the working day are we? 0 = before 5am, 1 = after 5pm.
    let progress = 0;
    if (built >= todayStart && built <= progressEnd) {
      progress = (built.getTime() - todayStart.getTime()) / (PROGRESS_MINUTES * 60000);
    } else if (built > progressEnd) {
      progress = 1;
    }
    const beforeWork = built < todayStart;
    // How many entries should be from "today"?
    // - Sunday: cap heavily — people aren't ordering as much. Max 2.
    // - Other days: scale with progress, but reserve at least 2 slots
    //   for prior-day orders so the feed doesn't feel one-day flat.
    let todayCount = 0;
    if (beforeWork) {
      todayCount = 0;
    } else if (isSunday) {
      todayCount = Math.min(2, Math.round(progress * 3));
    } else {
      todayCount = Math.min(total - 2, Math.round(progress * total));
    }
    // Remaining slots split across prior days.
    // - Sunday → Saturday + Friday (60/40)
    // - Monday → Sunday + Saturday (Sun fewer, so 30/70 toward Saturday)
    // - Other → almost all from yesterday, with 1 slot bleeding into 2 days ago
    const remaining = total - todayCount;
    const priorDays: {
      daysAgo: number;
      count: number;
    }[] = [];
    if (isSunday) {
      const satCount = Math.round(remaining * 0.6);
      priorDays.push({
        daysAgo: 1,
        count: satCount
      }); // Saturday
      priorDays.push({
        daysAgo: 2,
        count: remaining - satCount
      }); // Friday
    } else if (dow === 1) {
      // Monday — Sunday was quiet
      const sunCount = Math.round(remaining * 0.3);
      priorDays.push({
        daysAgo: 1,
        count: sunCount
      }); // Sunday
      priorDays.push({
        daysAgo: 2,
        count: remaining - sunCount
      }); // Saturday
    } else {
      // Most of remaining = yesterday, last one from 2 days ago for variety
      const olderCount = remaining > 4 ? 1 : 0;
      priorDays.push({
        daysAgo: 1,
        count: remaining - olderCount
      });
      if (olderCount > 0) priorDays.push({
        daysAgo: 2,
        count: olderCount
      });
    }
    type Stamped = (typeof baseActivities)[number] & {
      ts: Date;
    };
    const stamped: Stamped[] = [];
    let activityIdx = 0;
    // Helper: pick a "natural" order time within a given day, strictly
    // within business hours (6am–6pm) with peak density 8am–5pm.
    // No late-night stragglers — every order looks like it was placed
    // during dispatch hours.
    const buildPriorDayTime = (daysAgo: number) => {
      const day = new Date(built);
      day.setDate(day.getDate() - daysAgo);
      // Random minute within 6am–6pm, biased toward 8am–5pm (middle 80%).
      const frac = 0.1 + Math.random() * 0.8;
      const minutesFromStart = Math.round(frac * ACTIVE_MINUTES);
      day.setHours(ACTIVE_START_HOUR, 0, 0, 0);
      day.setMinutes(minutesFromStart);
      return day;
    };
    // TODAY orders — generate randomized "minutes ago" offsets so two
    // page loads in a row don't show identical timestamps. Newest entry
    // is always within the last 1–9 minutes (feels fresh). Subsequent
    // entries spread randomly across the elapsed work day.
    const todayMinutesElapsed = Math.max(1, Math.min(ACTIVE_MINUTES, Math.round((built.getTime() - todayStart.getTime()) / 60000)));
    const todayOffsets: number[] = [];
    if (todayCount > 0) {
      // Newest order: 1–9 min ago.
      todayOffsets.push(1 + Math.floor(Math.random() * 9));
      // Remaining: random spread across the rest of the elapsed window.
      const tail = Math.max(15, todayMinutesElapsed);
      for (let i = 1; i < todayCount; i++) {
        // Uniform over [10 min, todayMinutesElapsed], with light bias toward
        // the most recent half.
        const u = Math.random() ** 0.85;
        todayOffsets.push(10 + Math.round(u * (tail - 10)));
      }
      todayOffsets.sort((a, b) => a - b);
    }
    for (let i = 0; i < todayCount; i++) {
      const ts = new Date(built);
      ts.setMinutes(ts.getMinutes() - todayOffsets[i]);
      stamped.push({
        ...shuffledActivities[activityIdx % total],
        ts
      });
      activityIdx++;
    }
    // PRIOR-DAY orders — random "natural" times within each prior day.
    for (const pd of priorDays) {
      for (let i = 0; i < pd.count; i++) {
        const ts = buildPriorDayTime(pd.daysAgo);
        stamped.push({
          ...shuffledActivities[activityIdx % total],
          ts
        });
        activityIdx++;
      }
    }
    // Sort by recency, newest first.
    stamped.sort((a, b) => b.ts.getTime() - a.ts.getTime());
    return stamped;
  }, []);
  // Random starting position so each refresh shows a different first entry.
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * Math.max(1, baseActivities.length)));
  useEffect(() => {
    // Fast rotation during dispatch hours (Mon–Fri 6am–5pm, Sat 7am–3pm).
    // Outside that window orders get rare — slow the ticker by 8x so we
    // don't fake a steady stream of late-night gravel orders.
    const getInterval = () => {
      const now = new Date();
      const day = now.getDay(); // 0=Sun, 6=Sat
      const hour = now.getHours();
      let active = false;
      if (day === 0) active = false; // Sunday closed
      else if (day === 6) active = hour >= 7 && hour < 15; // Sat 7AM-3PM
      else active = hour >= 6 && hour < 17; // Mon-Fri 6AM-5PM
      return active ? 18000 : 144000; // 8x slower off-hours
    };
    let timeoutId: ReturnType<typeof setTimeout>;
    const tick = () => {
      setIdx(p => (p + 1) % activities.length);
      timeoutId = setTimeout(tick, getInterval());
    };
    timeoutId = setTimeout(tick, getInterval());
    return () => clearTimeout(timeoutId);
  }, [activities.length]);
  const current = activities[idx];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 0.6,
    duration: 0.8
  }} className="mt-9 flex items-center justify-center md:justify-start" data-id="element-643">
      <div className="inline-flex items-center gap-3 px-4 py-1.5 bg-stone-900/10 dark:bg-white/5 backdrop-blur-md border-l-2 border-[#1DB954] rounded-r-lg max-w-full" data-id="element-644">
        <div className="flex items-center gap-2 shrink-0" data-id="element-645">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#1DB954]" data-id="element-646">
            Live
          </span>
          <div className="flex items-end gap-0.5 h-3" data-id="element-647">
            {[0, 1, 2].map(i => <motion.span key={i} className="w-0.5 bg-[#1DB954] rounded-full" animate={{
            height: ['30%', '100%', '30%']
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.2,
            ease: 'easeInOut'
          }} data-id="element-648" />)}
          </div>
        </div>
        <span className="text-stone-400 dark:text-white/30" data-id="element-649">·</span>
        <AnimatePresence mode="wait" data-id="element-650">
          <motion.div key={idx} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.35
        }} className="text-xs font-medium truncate" data-id="element-651">
            <span className="font-[600] font-[Cabin,_sans-serif] text-[#6AAA7F] text-[12px]" data-id="element-652">
              {current.qty} yds {current.material}
            </span>
            <span className="text-[#17A347]" data-id="element-653"> → </span>
            <span className="text-[#5F9C7B]" data-id="element-654">{current.city}</span>
            <span className="text-[#5A9F72]" data-id="element-655">
              {' · '}
              {formatRelative(current.ts, now)}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>;
}
// Build a flat product map and a grouped option list for the dropdown.
// Categories become disabled headers; each product is a selectable option
// so users can pick the exact material they need (and we can quote an
// exact per-yard price instead of a category range).
const categoryColors: Record<string, {
  headerBg: string;
  headerText: string;
  rowAccent: string;
}> = {
  'Base & Compaction': {
    headerBg: 'bg-stone-200 dark:bg-stone-700/40',
    headerText: 'text-stone-700 dark:text-stone-200',
    rowAccent: 'border-l-stone-400 dark:border-l-stone-500'
  },
  'Clean Drain Rock': {
    headerBg: 'bg-sky-100 dark:bg-sky-900/30',
    headerText: 'text-sky-800 dark:text-sky-200',
    rowAccent: 'border-l-sky-400 dark:border-l-sky-500'
  },
  'Soil, Mulch & Org.': {
    headerBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    headerText: 'text-emerald-800 dark:text-emerald-200',
    rowAccent: 'border-l-emerald-400 dark:border-l-emerald-500'
  },
  'Landscape & Decor': {
    headerBg: 'bg-amber-100 dark:bg-amber-900/30',
    headerText: 'text-amber-800 dark:text-amber-200',
    rowAccent: 'border-l-amber-400 dark:border-l-amber-500'
  },
  'Quarry Rock': {
    headerBg: 'bg-slate-200 dark:bg-slate-700/40',
    headerText: 'text-slate-700 dark:text-slate-200',
    rowAccent: 'border-l-slate-400 dark:border-l-slate-500'
  }
};
type ProductInfo = {
  pricePerYard: number;
  slug: string;
  categoryName: string;
};
const productMap: Record<string, ProductInfo> = {};
const materialOptions: {
  value: string;
  label: string;
  disabled?: boolean;
  isHeader?: boolean;
  categoryName?: string;
}[] = [];
categories.forEach(cat => {
  materialOptions.push({
    value: `__cat__${cat.name}`,
    label: cat.name,
    disabled: true,
    isHeader: true,
    categoryName: cat.name
  });
  cat.products.forEach(p => {
    productMap[p.name] = {
      pricePerYard: p.pricePerYard,
      slug: p.slug,
      categoryName: cat.name
    };
    materialOptions.push({
      value: p.name,
      label: `${p.name} — $${p.pricePerYard}/yd`,
      categoryName: cat.name
    });
  });
});
// Lightweight materials (bark, compost, soil, play chips) fit more cubic
// yards per truckload than dense aggregates. Used for full-load sizing
// and small-load surcharge thresholds.
const isLightweight = (productName: string) => {
  return productMap[productName]?.categoryName === 'Soil, Mulch & Org.';
};
// Build a map of category name → { min, max } pricePerYard
const categoryPriceMap: Record<string, {
  min: number;
  max: number;
}> = {};
categories.forEach(cat => {
  const prices = cat.products.map(p => p.pricePerYard);
  categoryPriceMap[cat.name] = {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
});
// Add bark-specific pricing (Fine Bark, Medium Bark, Coarse Bark are all $45)
const barkProducts = categories.flatMap(cat => cat.products).filter(p => p.name.toLowerCase().includes('bark'));
if (barkProducts.length > 0) {
  const barkPrices = barkProducts.map(p => p.pricePerYard);
  categoryPriceMap['Bark'] = {
    min: Math.min(...barkPrices),
    max: Math.max(...barkPrices)
  };
}
const heroImages = ['https://cdn.magicpatterns.com/uploads/63NRWa6QZfPUMxTtMMK5Wv/file_0000000026f471f5b9d6d2c535161e4f.png', 'https://cdn.magicpatterns.com/uploads/824wZoPcAij7yeGBiywpRR/file_00000000e00c722f85d5870ff5890903.png', 'https://cdn.magicpatterns.com/uploads/p6WQ2rwEhGVV5j3eE1iAyz/file_00000000128c71fd996dc4dabfafdaa5~2.png'];
// Live deliveries counter — base 900 on a fixed anchor date, then adds
// a deterministic 10–20 deliveries per business day, plus partial progress
// for the current working day. Updates every minute.
const DELIVERIES_ANCHOR = new Date('2025-05-01T00:00:00');
const DELIVERIES_BASE = 900;
function seededRand(seed: number) {
  // Simple deterministic hash → [0,1)
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}
function isBusinessDay(d: Date) {
  const day = d.getDay();
  return day >= 1 && day <= 6; // Mon-Sat
}
function workingMinutesInDay(d: Date) {
  const day = d.getDay();
  if (day === 0) return 0;
  if (day === 6) return 8 * 60; // Sat 7-3
  return 11 * 60; // Mon-Fri 6-5
}
function workingMinutesElapsed(d: Date) {
  const day = d.getDay();
  const minutes = d.getHours() * 60 + d.getMinutes();
  if (day === 0) return 0;
  if (day === 6) {
    if (minutes < 7 * 60) return 0;
    if (minutes >= 15 * 60) return 8 * 60;
    return minutes - 7 * 60;
  }
  if (minutes < 6 * 60) return 0;
  if (minutes >= 17 * 60) return 11 * 60;
  return minutes - 6 * 60;
}
function dailyDelivery(d: Date) {
  // 10–20 inclusive, deterministic per date
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  return 10 + Math.floor(seededRand(seed) * 11);
}
function computeDeliveriesCount(now: Date) {
  let total = DELIVERIES_BASE;
  const cursor = new Date(DELIVERIES_ANCHOR);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  while (cursor < today) {
    if (isBusinessDay(cursor)) total += dailyDelivery(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  // Partial progress through today
  if (isBusinessDay(now)) {
    const todayTotal = dailyDelivery(now);
    const elapsed = workingMinutesElapsed(now);
    const fullDay = workingMinutesInDay(now);
    if (fullDay > 0) {
      total += Math.floor(todayTotal * (elapsed / fullDay));
    }
  }
  return total;
}
function useLiveDeliveries() {
  const [count, setCount] = useState(() => computeDeliveriesCount(new Date()));
  useEffect(() => {
    const update = () => setCount(computeDeliveriesCount(new Date()));
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);
  return count;
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
import { getZoneForPosition } from '../data/deliveryZones';
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee,
    label: result.zone > 0 ? `${result.price} Delivery` : 'Custom Quote'
  };
}
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
    isHeader?: boolean;
    categoryName?: string;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-656">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`estimator-input w-full px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1DB954] transition-all flex items-center justify-between ${value ? 'text-stone-900 dark:text-white' : 'text-stone-500 dark:text-white/50'}`} data-id="element-657">
        <span className="truncate" data-id="element-658">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-659" />
      </button>

      <AnimatePresence data-id="element-660">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full mt-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-661">
            {options.map(opt => {
          const colors = opt.categoryName ? categoryColors[opt.categoryName] : null;
          if (opt.isHeader && colors) {
            return <div key={opt.value} className={`px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.12em] ${colors.headerBg} ${colors.headerText} border-t border-stone-200 dark:border-white/5 first:border-t-0 select-none`} data-id="element-662">
                    {opt.label}
                  </div>;
          }
          return <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
            if (!opt.disabled) {
              onChange(opt.value);
              setIsOpen(false);
            }
          }} className={`w-full text-left pl-5 pr-4 py-3 text-sm border-l-4 transition-colors ${colors ? colors.rowAccent : 'border-l-transparent'} ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#1DB954]/20 text-[#1DB954] font-semibold' : 'text-stone-900 dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-663">
                  {opt.label}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Hero() {
  const estimatorRef = useRef<HTMLDivElement>(null);
  const quoteResultRef = useRef<HTMLDivElement>(null);
  const {
    addToCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  const [address, setAddress] = useState('');
  const [material, setMaterial] = useState('5/8" Minus');
  // Most orders are full loads — preselect 12yd so users only need to confirm.
  // Switches automatically to 15 below if a lightweight material is chosen.
  const [quantity, setQuantity] = useState('12');
  const [numLoads, setNumLoads] = useState('');
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    priceLow: number;
    priceHigh: number;
    address: string;
    quantity: string;
    material: string;
    materialSlug?: string;
    deliveryZone: number | null;
    deliveryFee: number;
    deliveryLabel: string;
    smallLoadSurcharge: number;
    effectivePricePerYardLow: number;
    effectivePricePerYardHigh: number;
    basePricePerYardLow: number;
    basePricePerYardHigh: number;
    deliveries?: number;
    bulkDiscount?: number;
    // When a custom yardage is split across loads, the last load may fall
    // below the 5yd per-load minimum and gets billed up to 5yd. This field
    // captures that adjustment so we can surface a transparent warning.
    minLoadAdjustment?: {
      requested: number;
      billable: number;
      lastLoadActual: number;
    } | null;
  } | null>(null);
  // Toggle for the "why am I billed for more yards?" info popover.
  const [showMinLoadInfo, setShowMinLoadInfo] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [heroImage] = useState(() => {
    try {
      const key = 'hero_image_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % heroImages.length;
      sessionStorage.setItem(key, String(index + 1));
      return heroImages[index];
    } catch {
      return heroImages[0];
    }
  });
  const [showEstimator, setShowEstimator] = useState(true);
  // Address-first flow: address is the primary, always-visible input.
  // Once it geocodes successfully, material + quantity row reveals.
  const [addressZone, setAddressZone] = useState<{
    zone: number | null;
    fee: number;
    label: string;
  } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [showMaterialRow, setShowMaterialRow] = useState(false);
  // Custom yardage produced by the inline calculator (allows non-standard
  // single-trip quantities like 13yd, which auto-split across deliveries
  // in pricing math but display as a single line item in the quantity menu).
  const [customQty, setCustomQty] = useState<number | null>(null);
  // Inline "Not sure?" calculator state for the How Much step
  const [showHeroCalc, setShowHeroCalc] = useState(false);
  const [heroCalcShape, setHeroCalcShape] = useState<'rectangle' | 'circle' | 'triangle' | 'irregular'>('rectangle');
  const [heroCalcLength, setHeroCalcLength] = useState('');
  const [heroCalcWidth, setHeroCalcWidth] = useState('');
  const [heroCalcDiameter, setHeroCalcDiameter] = useState('');
  const [heroCalcBase, setHeroCalcBase] = useState('');
  const [heroCalcHeight, setHeroCalcHeight] = useState('');
  const [heroCalcDepth, setHeroCalcDepth] = useState('3');
  const t = useT();
  const {
    locale
  } = useLocale();
  const liveDeliveries = useLiveDeliveries();
  // Seasonal messaging — rotates between 2 messages based on season, cycling on refresh
  const springSummerMessages = [{
    headlineLine1: 'Spring Into Action.',
    headlineLine2: 'Fresh Starts Begin Here.',
    subtitle: 'The smartest way to order topsoil, bark mulch, and garden prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Build Season Is Here.',
    headlineLine2: 'Your Project Starts Now.',
    subtitle: 'The smartest way to order materials for driveways, patios, and landscaping — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Sun's Out, Trucks Out.",
    headlineLine2: "Let's Get It Done.",
    subtitle: 'The smartest way to order gravel, topsoil, and landscaping materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: "Your Yard Won't Wait.",
    headlineLine2: 'Neither Will We.',
    subtitle: 'The smartest way to order bark, compost, and garden-ready materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const fallWinterMessages = [{
    headlineLine1: 'Prep Before the Rain.',
    headlineLine2: 'Fall Cleanup Made Easy.',
    subtitle: 'The smartest way to order drain rock, bark mulch, and grading materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'One Thumb.',
    headlineLine2: 'Full Driveway.',
    subtitle: 'The smartest way to order gravel, drainage, and winter prep materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Beat the Freeze.',
    headlineLine2: 'Gravel Up Before Winter.',
    subtitle: 'The smartest way to order crushed rock, drain rock, and fill materials — instant estimates and automatic bulk pricing from your phone.'
  }, {
    headlineLine1: 'Mud Season? Handled.',
    headlineLine2: 'Solid Ground Starts Here.',
    subtitle: 'The smartest way to order base rock, gravel, and drainage materials — instant estimates and automatic bulk pricing from your phone.'
  }];
  const [seasonalContent] = useState(() => {
    try {
      const month = new Date().getMonth(); // 0-11
      const isWarmSeason = month >= 2 && month <= 7; // Mar-Aug
      const pool = isWarmSeason ? springSummerMessages : fallWinterMessages;
      const key = 'hero_seasonal_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % pool.length;
      sessionStorage.setItem(key, String(stored + 1));
      return pool[index];
    } catch {
      return fallWinterMessages[1];
    }
  });
  // Debounced address geocoding — runs as user types. Once we get a valid zone,
  // we reveal the material + quantity row.
  useEffect(() => {
    if (!address.trim()) {
      setAddressZone(null);
      setAddressError('');
      setShowMaterialRow(false);
      return;
    }
    const timer = setTimeout(async () => {
      setIsGeocoding(true);
      setAddressError('');
      try {
        // Only append ", WA" if the address doesn't already include the state —
        // doubling the state reference can cause Nominatim to return no results
        // for otherwise valid addresses (e.g. "...Seattle, WA 98107").
        const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
        const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Try a more specific address.');
          setAddressZone(null);
          setShowMaterialRow(false);
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const info = getZoneInfo(lat, lon);
          setAddressZone(info);
          setShowMaterialRow(true);
        }
      } catch {
        setAddressError('Could not verify address. Please try again.');
        setShowMaterialRow(false);
      } finally {
        setIsGeocoding(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [address]);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address) return;
    setIsSearching(true);
    setQuoteResult(null);
    try {
      // Geocode the address — only append ", WA" if the state isn't already
      // in the user's input, to avoid Nominatim zero-result quirks on doubled
      // state references like "...Seattle, WA 98107".
      const alreadyHasWA = /\b(WA|Washington)\b/i.test(address);
      const query = encodeURIComponent(alreadyHasWA ? address : address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      let zoneInfo = {
        zone: null as number | null,
        fee: 0,
        label: 'Custom Quote'
      };
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        zoneInfo = getZoneInfo(lat, lon);
      }
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          // Custom amount from calculator that exceeds one truckload —
          // auto-split into the right number of deliveries. No small-load
          // surcharge applies since at least one truck is full.
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else if (light) {
          isSmallLoad = qty < 12;
          surchargeMultiplier = isSmallLoad ? 1 + (12 - qty) * 0.1 : 1;
        } else {
          isSmallLoad = qty < 10;
          surchargeMultiplier = isSmallLoad ? 1 + (10 - qty) * 0.1 : 1;
        }
      }
      // Apply 5yd-per-load minimum billing for custom quantities split
      // across multiple deliveries. The partial last load gets billed up
      // to 5yd if it would otherwise fall below — same rule as the cart.
      const MIN_LOAD = 5;
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        if (lastLoad < MIN_LOAD) {
          billableQty = (deliveries - 1) * fullLoadYards + MIN_LOAD;
          minAdjust = {
            requested: qty,
            billable: billableQty,
            lastLoadActual: lastLoad
          };
        }
      }
      const bulkPct = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct));
      const total = effective * billableQty + zoneInfo.fee * deliveries;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: zoneInfo.zone,
        deliveryFee: zoneInfo.fee * deliveries,
        deliveryLabel: zoneInfo.label,
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct,
        minLoadAdjustment: minAdjust
      });
    } catch {
      const productInfo = productMap[material];
      const basePrice = productInfo?.pricePerYard ?? 55;
      const light = isLightweight(material);
      const fullLoadYards = light ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (qty > fullLoadYards) {
          deliveries = Math.ceil(qty / fullLoadYards);
          isSmallLoad = false;
          surchargeMultiplier = 1;
        } else if (light) {
          isSmallLoad = qty < 12;
          surchargeMultiplier = isSmallLoad ? 1 + (12 - qty) * 0.1 : 1;
        } else {
          isSmallLoad = qty < 10;
          surchargeMultiplier = isSmallLoad ? 1 + (10 - qty) * 0.1 : 1;
        }
      }
      const MIN_LOAD = 5;
      let billableQty = qty;
      let minAdjust: {
        requested: number;
        billable: number;
        lastLoadActual: number;
      } | null = null;
      if (quantity !== 'multi' && deliveries > 1) {
        const lastLoad = qty - (deliveries - 1) * fullLoadYards;
        if (lastLoad < MIN_LOAD) {
          billableQty = (deliveries - 1) * fullLoadYards + MIN_LOAD;
          minAdjust = {
            requested: qty,
            billable: billableQty,
            lastLoadActual: lastLoad
          };
        }
      }
      const bulkPct2 = billableQty >= fullLoadYards * 4 ? 0.1 : billableQty >= (light ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      const effective = Math.ceil(basePrice * surchargeMultiplier * (1 - bulkPct2));
      const total = effective * billableQty + 100 * deliveries;
      setQuoteResult({
        priceLow: total,
        priceHigh: total,
        address,
        quantity: quantity === 'multi' ? String(qty) : String(billableQty),
        material,
        materialSlug: productInfo?.slug,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: 'Custom Quote',
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effective,
        effectivePricePerYardHigh: effective,
        basePricePerYardLow: basePrice,
        basePricePerYardHigh: basePrice,
        deliveries,
        bulkDiscount: bulkPct2,
        minLoadAdjustment: minAdjust
      });
    }
    setIsSearching(false);
    setTimeout(() => {
      if (quoteResultRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 80;
        const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      }
    }, 250);
  };
  const handleCloseEstimator = () => {
    setShowEstimator(false);
    setShowMaterialRow(false);
    setMaterial('5/8" Minus');
    setQuantity('12');
    setNumLoads('');
    setQuoteResult(null);
    setAddress('');
    setAddressZone(null);
    setAddressError('');
  };
  // One-tap: add the quoted material to the cart, persist delivery info, open cart drawer.
  // Lets a finished estimator flow straight into checkout without re-entering address.
  // For multi-load orders, split into separate cart line items so the user sees
  // a clear breakdown (and the small-load surcharge applies correctly to the
  // partial last load).
  const handleAddToTruckFromQuote = () => {
    if (!quoteResult) return;
    const productInfo = productMap[quoteResult.material];
    if (!productInfo) return;
    const finalQuantity = parseInt(quoteResult.quantity) || 0;
    if (finalQuantity <= 0) return;
    const deliveries = quoteResult.deliveries || 1;
    const light = isLightweight(quoteResult.material);
    const fullLoad = light ? 15 : 12;
    const MIN_LOAD = 5;
    if (deliveries > 1) {
      // Split across N deliveries: full loads for all but last, remainder
      // (min 5yd) for the last. Each load is its own cart line item so the
      // user sees the breakdown and small-load surcharges apply naturally.
      const firstLoadsTotal = (deliveries - 1) * fullLoad;
      const lastLoad = Math.max(finalQuantity - firstLoadsTotal, MIN_LOAD);
      for (let i = 0; i < deliveries; i++) {
        const isLast = i === deliveries - 1;
        const qty = isLast ? lastLoad : fullLoad;
        addToCart({
          category: productInfo.categoryName,
          product: `${quoteResult.material} (Delivery ${i + 1})`,
          quantity: qty,
          pricePerYard: productInfo.pricePerYard,
          source: 'estimator'
        });
      }
    } else {
      addToCart({
        category: productInfo.categoryName,
        product: quoteResult.material,
        quantity: finalQuantity,
        pricePerYard: productInfo.pricePerYard,
        source: 'estimator'
      });
    }
    // Persist the address + zone + fee so CartDrawer doesn't ask again.
    setDeliveryInfo(quoteResult.address, quoteResult.deliveryFee && quoteResult.deliveries ? Math.round(quoteResult.deliveryFee / quoteResult.deliveries) : 0, quoteResult.deliveryZone);
    openCart();
  };
  return <section className="relative w-full bg-stone-200 dark:bg-[#2a2a2a] py-14 md:py-40 overflow-hidden" style={{
    marginTop: '-149px',
    paddingTop: 'calc(149px + 3.5rem)'
  }} data-id="element-664">
      {/* Background Image - extends behind navbar */}
      <motion.div className="absolute inset-0 z-0 bg-cover bg-center" style={{
      backgroundImage: `url(${heroImage})`,
      top: 0
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.5,
      ease: 'easeInOut'
    }} data-id="element-665" />
      {/* Black overlays — same in light & dark mode */}
      <div className="absolute inset-0 z-0 md:hidden bg-black/50" data-id="element-666" />
      <div className="absolute inset-0 z-0 hidden md:block bg-gradient-to-r from-black/70 via-black/40 to-black/10" data-id="element-667" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-668">
        {/* Personalized greeting for returning visitors — only renders if a
            saved last-order exists in localStorage and the user hasn't
            dismissed it this session. */}
        <WelcomeBackCard data-id="element-669" />

        <div className="text-center md:text-left mb-8 md:mb-12 max-w-3xl" data-id="element-670">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="flex items-center justify-center md:justify-start mb-4 md:mb-6" data-id="element-671">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-none bg-stone-900/10 dark:bg-white/10 backdrop-blur-sm border border-stone-900/15 dark:border-white/15 text-stone-800 dark:text-white/90 text-xs sm:text-sm font-semibold tracking-widest uppercase" data-id="element-672">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1DB954] animate-pulse" data-id="element-673" />
              Order Gravel Like It's 2026
            </span>
          </motion.div>

          <AnimatePresence mode="wait" data-id="element-674">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} data-id="element-675">
              <div className="w-12 md:w-16 h-1 md:h-1.5 bg-[#1DB954] mb-4 md:mb-6 mx-auto md:mx-0 rounded-full" data-id="element-676" />
              <AnimatePresence mode="wait" data-id="element-677">
                <motion.h1 key={locale + seasonalContent.headlineLine1} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 md:mb-6 text-stone-900 dark:text-white leading-[1.1]" data-id="element-678">
                  {locale === 'es' ? t('hero.headlineLine1') : seasonalContent.headlineLine1}
                  <span className="text-[#1DB954] block mt-1 md:mt-2" data-id="element-679">
                    {locale === 'es' ? t('hero.headlineLine2') : seasonalContent.headlineLine2}
                  </span>
                </motion.h1>
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait" data-id="element-680">
            <motion.p key={locale + seasonalContent.subtitle} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3,
            delay: 0.1
          }} className="text-lg md:text-xl text-stone-700 dark:text-white/90 font-semibold font-sub mb-5 md:mb-6 max-w-2xl mx-auto md:mx-0" data-id="element-681">
              {locale === 'es' ? t('hero.subtitle') : seasonalContent.subtitle}
            </motion.p>
          </AnimatePresence>

          {/* Review Badge + Feature Pills */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.25
        }} className="mb-3 md:mb-5 space-y-2" data-id="element-682">
            <div className="flex items-center gap-2 justify-center md:justify-start" data-id="element-683">
              <div className="flex items-center gap-1.5 bg-stone-900/10 dark:bg-white/10 backdrop-blur-md border border-stone-900/15 dark:border-white/20 rounded-full px-4 py-2" data-id="element-684">
                <div className="flex" data-id="element-685">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-686" />)}
                </div>
                <span className="text-stone-400 dark:text-white/50 text-sm mx-1" data-id="element-687">
                  ·
                </span>
                <span className="text-stone-700 dark:text-white/80 text-sm font-medium tabular-nums" data-id="element-688">
                  {liveDeliveries.toLocaleString()}+ {t('hero.deliveriesWord')}
                </span>
              </div>
            </div>
            {/* Feature pills hide when estimator is open to reduce clutter */}
            <AnimatePresence data-id="element-689">
              {!showEstimator && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.25
            }} className="flex flex-wrap gap-2 justify-center md:justify-start overflow-hidden" data-id="element-690">
                  {[{
                icon: '⚡',
                label: t('hero.feat.estimates')
              }, {
                icon: '🧮',
                label: t('hero.feat.bulk')
              }, {
                icon: '📊',
                label: t('hero.feat.compare')
              }].map(pill => <span key={pill.label} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium backdrop-blur-sm border ${pill.highlight ? 'bg-[#1DB954]/15 dark:bg-[#1DB954]/15 border-[#1DB954]/40 dark:border-[#1DB954]/50 text-stone-900 dark:text-white' : 'bg-stone-900/5 dark:bg-white/5 border-stone-900/10 dark:border-white/10 text-stone-700 dark:text-white/80'}`} data-id="element-691">
                      <span data-id="element-692">{pill.icon}</span>
                      {pill.label}
                    </span>)}
                </motion.div>}
            </AnimatePresence>
          </motion.div>

          {/* CTA Buttons */}
          {!showEstimator && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.3
        }} className="flex flex-col items-center md:items-start gap-3" data-id="element-693">
              <button onClick={() => {
            setShowEstimator(true);
            setTimeout(() => {
              if (estimatorRef.current) {
                const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 80;
                window.scrollTo({
                  top,
                  behavior: 'smooth'
                });
              }
            }, 150);
          }} className="w-full sm:w-auto px-8 py-5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/25 hover:border-white/40 text-white rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center" data-id="element-694">
                {t('hero.getEstimate')}
                <ArrowRight className="w-5 h-5" data-id="element-695" />
              </button>
              <button type="button" onClick={() => {
            const el = document.getElementById('materials');
            if (el) {
              const navHeight = document.querySelector('nav')?.offsetHeight || 80;
              const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
              window.scrollTo({
                top,
                behavior: 'smooth'
              });
            }
          }} className="group inline-flex items-center text-sm font-medium text-stone-700 dark:text-white/70 hover:text-[#1DB954] dark:hover:text-[#1DB954] transition-colors" data-id="element-696">
                <span className="border-b border-current/30 group-hover:border-current pb-0.5" data-id="element-697">
                  Or browse the full catalog
                </span>
                <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-698" />
              </button>
            </motion.div>}
        </div>

        {/* Lead-in label above the estimator on page load */}
        <AnimatePresence data-id="element-699">
          {showEstimator && <motion.div key="estimator-leadin" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.3,
          delay: 0.1
        }} className="flex items-center justify-center md:justify-start gap-2 mb-4 max-w-3xl mx-auto md:mx-0" data-id="element-700">
              <CheckCircle2 className="w-4 h-4 text-[#1DB954] shrink-0" data-id="element-701" />
              <span className="text-sm font-medium text-white" data-id="element-702">
                Pay on delivery, no card needed
              </span>
            </motion.div>}
        </AnimatePresence>

        {/* Estimator Card */}
        <AnimatePresence data-id="element-703">
          {showEstimator && <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} ref={estimatorRef} className="estimator-card relative z-20 rounded-2xl p-6 sm:p-8 mb-10 max-w-3xl mx-auto md:mx-0 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] scroll-mt-[180px] bg-black/40 backdrop-blur-xl border border-white/20" data-id="element-704">
              <button onClick={handleCloseEstimator} className="absolute top-4 right-4 p-2 text-stone-700 dark:text-white/60 hover:text-stone-900 dark:hover:text-white transition-colors" data-id="element-705">
                <X className="w-5 h-5" data-id="element-706" />
              </button>

              <p className="estimator-label text-sm font-bold uppercase tracking-widest mb-4 flex items-center" data-id="element-707">
                <span className="w-8 h-px bg-current opacity-70 mr-3" data-id="element-708"></span>
                {t('hero.estimator.label')}
              </p>

              {/* Returning visitor: slim one-tap reorder chip above the form.
               Renders nothing for first-time visitors. */}
              <div className="mb-4" data-id="element-709">
                <ReorderChip data-id="element-710" />
              </div>

              <form onSubmit={handleSearch} className="space-y-4" data-id="element-711">
                {/* Step 1: Address (always first) */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-2" data-id="element-712">
                  <label className="estimator-label flex items-center text-xs font-bold uppercase tracking-wider gap-1.5" data-id="element-713">
                    <MapPin className="w-3.5 h-3.5 text-[#1DB954]" data-id="element-714" />
                    {t('hero.estimator.step1')}
                  </label>
                  <div className="relative" data-id="element-715">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-716">
                      <MapPin className="h-4 w-4 text-stone-700 dark:text-white/60" data-id="element-717" />
                    </div>
                    <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder={t('hero.estimator.addressPlaceholder')} autoComplete="street-address" className="estimator-input block w-full pl-10 pr-10 py-3.5 rounded-lg text-base text-stone-900 dark:text-white placeholder-stone-500 dark:placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#1DB954] transition-all" required data-id="element-718" />
                    {isGeocoding && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-719">
                        <div className="w-4 h-4 border-2 border-[#1DB954] border-t-transparent rounded-full animate-spin" data-id="element-720" />
                      </div>}
                    {!isGeocoding && addressZone?.zone && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none" data-id="element-721">
                        <CheckCircle2 className="w-4 h-4 text-[#1DB954]" data-id="element-722" />
                      </div>}
                  </div>
                  <AnimatePresence mode="wait" data-id="element-723">
                    {addressError && !isGeocoding && <motion.p key="err" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="text-xs text-red-500 dark:text-red-400 font-medium" data-id="element-724">
                        {addressError}
                      </motion.p>}
                    {addressZone && addressZone.zone !== null && !isGeocoding && <motion.div key="zone" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-xs font-medium" data-id="element-725">
                          <Link to={`/delivery-zones?address=${encodeURIComponent(address)}`} className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1DB954]/15 border border-[#1DB954]/30 text-[#1DB954] font-bold hover:bg-[#1DB954]/25 hover:border-[#1DB954]/50 transition-colors" title="View on delivery zones map" data-id="element-726">
                            {t('hero.quote.zone')} {addressZone.zone}
                            <MapPin className="w-3 h-3 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-727" />
                          </Link>
                          <span className="text-stone-700 dark:text-white/70" data-id="element-728">
                            {addressZone.fee === 0 ? t('hero.estimator.freeDelivery') : `+${addressZone.fee} ${t('hero.estimator.plusDelivery')}`}
                          </span>
                        </motion.div>}
                    {addressZone && addressZone.zone === null && !isGeocoding && <motion.p key="custom" initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className="text-xs text-brand-orange font-medium flex items-center gap-1.5" data-id="element-729">
                          <Info className="w-3.5 h-3.5" data-id="element-730" />
                          {t('hero.estimator.outsideZones')}
                        </motion.p>}
                  </AnimatePresence>
                </motion.div>

                {/* Step 2: Material */}
                <AnimatePresence data-id="element-731">
                  {showMaterialRow && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} className="space-y-2 overflow-visible" data-id="element-732">
                      <label className="estimator-label flex items-center text-xs font-bold uppercase tracking-wider gap-1.5" data-id="element-733">
                        <span className="w-3.5 h-3.5 rounded-full bg-[#1DB954]/20 text-[#1DB954] text-[10px] font-extrabold flex items-center justify-center" data-id="element-734">
                          2
                        </span>
                        What material?
                      </label>
                      <CustomSelect value={material} onChange={val => setMaterial(val)} placeholder="Select Material" options={materialOptions} data-id="element-735" />
                      <p className="text-[11px] text-stone-600 dark:text-white/60 pt-1" data-id="element-736">
                        Not sure what you need?{' '}
                        <Link to="/help" className="text-[#1DB954] font-semibold hover:underline" data-id="element-737">
                          Take the 30-second material quiz →
                        </Link>
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {/* Step 3: How much (quantity pill + "not sure?" calc pill) */}
                <AnimatePresence data-id="element-738">
                  {showMaterialRow && <motion.div initial={{
                opacity: 0,
                height: 0,
                y: -10
              }} animate={{
                opacity: 1,
                height: 'auto',
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35,
                delay: 0.05
              }} className="space-y-2 overflow-visible" data-id="element-739">
                      <label className="estimator-label flex items-center text-xs font-bold uppercase tracking-wider gap-1.5" data-id="element-740">
                        <span className="w-3.5 h-3.5 rounded-full bg-[#1DB954]/20 text-[#1DB954] text-[10px] font-extrabold flex items-center justify-center" data-id="element-741">
                          3
                        </span>
                        How much do you need?
                      </label>
                      <div className="grid grid-cols-4 gap-2" data-id="element-742">
                        <div className="col-span-3" data-id="element-743">
                          <CustomSelect value={quantity} onChange={val => {
                      setQuantity(val);
                      if (val !== 'multi') setNumLoads('');
                      if (!customQty || val !== String(customQty)) {
                        setCustomQty(null);
                      }
                    }} placeholder="Quantity" options={(() => {
                      const light = isLightweight(material);
                      const standard = light ? [{
                        value: '15',
                        label: '15 Yards (Full Load)'
                      }, {
                        value: '14',
                        label: '14 Yards'
                      }, {
                        value: '13',
                        label: '13 Yards'
                      }, {
                        value: '12',
                        label: '12 Yards'
                      }, {
                        value: '11',
                        label: '11 Yards'
                      }, {
                        value: '10',
                        label: '10 Yards'
                      }, {
                        value: '9',
                        label: '9 Yards'
                      }, {
                        value: '8',
                        label: '8 Yards'
                      }, {
                        value: '7',
                        label: '7 Yards'
                      }, {
                        value: '6',
                        label: '6 Yards'
                      }, {
                        value: '5',
                        label: '5 Yards (Min)'
                      }, {
                        value: 'multi',
                        label: 'More than 1 load'
                      }] : [{
                        value: '12',
                        label: '12 Yards (Full Load)'
                      }, {
                        value: '11',
                        label: '11 Yards'
                      }, {
                        value: '10',
                        label: '10 Yards'
                      }, {
                        value: '9',
                        label: '9 Yards'
                      }, {
                        value: '8',
                        label: '8 Yards'
                      }, {
                        value: '7',
                        label: '7 Yards'
                      }, {
                        value: '6',
                        label: '6 Yards'
                      }, {
                        value: '5',
                        label: '5 Yards (Min)'
                      }, {
                        value: 'multi',
                        label: 'More than 1 load'
                      }];
                      if (customQty && !standard.some(o => o.value === String(customQty))) {
                        const fullLoad = light ? 15 : 12;
                        const loadsNeeded = Math.ceil(customQty / fullLoad);
                        return [{
                          value: String(customQty),
                          label: `${customQty} Yards (Calculated · ${loadsNeeded} loads)`
                        }, ...standard];
                      }
                      return standard;
                    })()} data-id="element-744" />
                        </div>
                        <button type="button" onClick={() => setShowHeroCalc(v => !v)} className={`estimator-input col-span-1 flex flex-col items-center justify-center gap-0.5 px-2 py-3 rounded-lg transition-all ${showHeroCalc ? 'bg-[#1DB954]/20 border-[#1DB954]/60 text-white' : 'hover:bg-white/10 text-white/80'}`} aria-expanded={showHeroCalc} aria-label={showHeroCalc ? 'Hide calculator' : 'Not sure? Open calculator'} title={showHeroCalc ? 'Hide calculator' : 'Not sure? Open calculator'} data-id="element-745">
                          <Calculator className="w-4 h-4" data-id="element-746" />
                          <span className="text-[10px] font-semibold leading-none" data-id="element-747">
                            {showHeroCalc ? 'Hide' : 'Not sure?'}
                          </span>
                        </button>
                      </div>

                      {/* Inline Calculator Drawer */}
                      <AnimatePresence data-id="element-748">
                        {showHeroCalc && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.25
                  }} className="overflow-hidden" data-id="element-749">
                            <div className="mt-2 p-4 rounded-xl bg-black/30 border border-white/15 space-y-3" data-id="element-750">
                              {/* Shape pills */}
                              <div className="grid grid-cols-4 gap-1.5" data-id="element-751">
                                {([{
                          id: 'rectangle',
                          label: 'Rect'
                        }, {
                          id: 'circle',
                          label: 'Circle'
                        }, {
                          id: 'triangle',
                          label: 'Tri'
                        }, {
                          id: 'irregular',
                          label: 'Other'
                        }] as const).map(s => <button key={s.id} type="button" onClick={() => setHeroCalcShape(s.id)} className={`py-2 rounded-lg text-xs font-bold transition-colors ${heroCalcShape === s.id ? 'bg-[#1DB954] text-white' : 'bg-white/5 border border-white/15 text-white/80 hover:bg-white/10'}`} data-id="element-752">
                                    {s.label}
                                  </button>)}
                              </div>

                              {/* Dimension inputs */}
                              {heroCalcShape === 'rectangle' || heroCalcShape === 'irregular' ? <div className="grid grid-cols-3 gap-2" data-id="element-753">
                                  <input type="number" value={heroCalcLength} onChange={e => setHeroCalcLength(e.target.value)} placeholder="Length (ft)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-754" />
                                  <input type="number" value={heroCalcWidth} onChange={e => setHeroCalcWidth(e.target.value)} placeholder="Width (ft)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-755" />
                                  <input type="number" value={heroCalcDepth} onChange={e => setHeroCalcDepth(e.target.value)} placeholder="Depth (in)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-756" />
                                </div> : heroCalcShape === 'circle' ? <div className="grid grid-cols-2 gap-2" data-id="element-757">
                                  <input type="number" value={heroCalcDiameter} onChange={e => setHeroCalcDiameter(e.target.value)} placeholder="Diameter (ft)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-758" />
                                  <input type="number" value={heroCalcDepth} onChange={e => setHeroCalcDepth(e.target.value)} placeholder="Depth (in)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-759" />
                                </div> : <div className="grid grid-cols-3 gap-2" data-id="element-760">
                                  <input type="number" value={heroCalcBase} onChange={e => setHeroCalcBase(e.target.value)} placeholder="Base (ft)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-761" />
                                  <input type="number" value={heroCalcHeight} onChange={e => setHeroCalcHeight(e.target.value)} placeholder="Height (ft)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-762" />
                                  <input type="number" value={heroCalcDepth} onChange={e => setHeroCalcDepth(e.target.value)} placeholder="Depth (in)" className="estimator-input w-full px-3 py-2 rounded-lg text-sm" min="0" step="any" data-id="element-763" />
                                </div>}

                              {/* Live result + Use button */}
                              {(() => {
                        const l = parseFloat(heroCalcLength) || 0;
                        const w = parseFloat(heroCalcWidth) || 0;
                        const dia = parseFloat(heroCalcDiameter) || 0;
                        const b = parseFloat(heroCalcBase) || 0;
                        const h = parseFloat(heroCalcHeight) || 0;
                        const dpIn = parseFloat(heroCalcDepth) || 0;
                        let areaSqFt = 0;
                        if (heroCalcShape === 'rectangle' || heroCalcShape === 'irregular') {
                          areaSqFt = l * w;
                        } else if (heroCalcShape === 'circle') {
                          const r = dia / 2;
                          areaSqFt = Math.PI * r * r;
                        } else {
                          areaSqFt = 0.5 * b * h;
                        }
                        const cubicYards = areaSqFt * (dpIn / 12) / 27;
                        const hasInput = areaSqFt > 0 && dpIn > 0;
                        if (!hasInput) {
                          return <p className="text-[11px] text-white/60 text-center pt-1" data-id="element-764">
                                      Enter your area to see how many yards you
                                      need.
                                    </p>;
                        }
                        const light = isLightweight(material);
                        const fullLoad = light ? 15 : 12;
                        const rounded = Math.max(5, Math.ceil(cubicYards));
                        // Cap custom amounts at 4 full loads —
                        // anything larger pushes to custom quote.
                        const maxCustom = fullLoad * 4;
                        const handleUse = () => {
                          if (rounded > maxCustom) {
                            setQuantity('multi');
                            setNumLoads('3+');
                            setCustomQty(null);
                          } else if (rounded <= fullLoad) {
                            setQuantity(String(rounded));
                            setNumLoads('');
                            setCustomQty(null);
                          } else {
                            // Custom amount above single-load capacity:
                            // store the exact figure & inject it as a
                            // dropdown option. Pricing math auto-splits
                            // it across the right number of deliveries.
                            setCustomQty(rounded);
                            setQuantity(String(rounded));
                            setNumLoads('');
                          }
                          setShowHeroCalc(false);
                        };
                        return <div className="pt-2 border-t border-white/10 space-y-2" data-id="element-765">
                                    <div className="flex items-center justify-between" data-id="element-766">
                                      <span className="text-xs text-white/70" data-id="element-767">
                                        You need approximately
                                      </span>
                                      <span className="text-xl font-bold text-white" data-id="element-768">
                                        {cubicYards.toFixed(1)} yds
                                      </span>
                                    </div>
                                    <button type="button" onClick={handleUse} className="w-full py-2.5 bg-[#1DB954] hover:bg-[#17A347] text-white rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2" data-id="element-769">
                                      <CheckCircle2 className="w-4 h-4" data-id="element-770" />
                                      Use {rounded} yards
                                    </button>
                                  </div>;
                      })()}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>}
                </AnimatePresence>

                {/* Multi-load selector */}
                <AnimatePresence data-id="element-771">
                  {showMaterialRow && quantity === 'multi' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-visible" data-id="element-772">
                      <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                  value: '2',
                  label: '2 Loads'
                }, {
                  value: '3',
                  label: '3 Loads'
                }, {
                  value: '3+',
                  label: '3+ Loads (Custom Quote)'
                }]} data-id="element-773" />
                      <div className="flex items-start gap-2 p-3 bg-[#1DB954]/10 border border-[#1DB954]/20 rounded-lg mt-3" data-id="element-774">
                        <Info className="w-4 h-4 text-[#1DB954] mt-0.5 shrink-0" data-id="element-775" />
                        <p className="text-xs text-stone-700 dark:text-white/90" data-id="element-776">
                          Multi-load orders require separate deliveries. You
                          will be charged a delivery fee per load.
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Submit button — only when address + material/quantity are ready */}
                <AnimatePresence data-id="element-777">
                  {showMaterialRow && quantity && (quantity !== 'multi' || numLoads) && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-778">
                        <button type="submit" disabled={isSearching || !address} className="w-full bg-[#1DB954] hover:bg-[#17A347] text-white px-6 py-3.5 rounded-lg font-bold text-base transition-colors flex items-center justify-center disabled:opacity-70 shadow-[0_0_20px_rgba(29,185,84,0.25)]" data-id="element-779">
                          {isSearching ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-780"></div> : <>
                              Get Instant Estimate
                              <ArrowRight className="w-4 h-4 ml-2" data-id="element-781" />
                            </>}
                        </button>
                      </motion.div>}
                </AnimatePresence>
              </form>

              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs text-stone-700 dark:text-white/60" data-id="element-782">
                <div className="flex items-center" data-id="element-783">
                  <Lock className="w-3 h-3 mr-1.5 opacity-70" data-id="element-784" />
                  Your information is secure
                </div>
                <span className="hidden sm:inline opacity-30" data-id="element-785">|</span>
                {(() => {
              // Compute current Pacific time + day. Dispatch hours:
              // Mon–Fri 5am–6pm, Sat 7am–3pm, Sun closed.
              const nowPT = new Date(new Date().toLocaleString('en-US', {
                timeZone: 'America/Los_Angeles'
              }));
              const day = nowPT.getDay(); // 0=Sun, 6=Sat
              const hour = nowPT.getHours();
              let isActive = false;
              let label = '';
              if (day === 0) {
                // Sunday — closed all day
                label = 'Dispatch Closed Sundays';
              } else if (day === 6) {
                isActive = hour >= 7 && hour < 15;
                label = isActive ? 'Dispatch Active' : 'Dispatch Opens 7 AM';
              } else {
                isActive = hour >= 5 && hour < 18;
                label = isActive ? 'Dispatch Active' : day === 5 && hour >= 18 ? 'Dispatch Opens Sat 7 AM' : 'Dispatch Opens 5 AM';
              }
              return <div className={`flex items-center gap-1.5 font-medium ${isActive ? 'text-green-600 dark:text-green-400' : 'text-stone-400 dark:text-white/30'}`} data-id="element-786">
                      <span className="relative flex h-2 w-2" data-id="element-787">
                        {isActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 dark:bg-green-400 opacity-75" data-id="element-788"></span>}
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? 'bg-green-500 dark:bg-green-400' : 'bg-stone-400 dark:bg-white/30'}`} data-id="element-789"></span>
                      </span>
                      {label}
                    </div>;
            })()}
              </div>

              {/* Quote Result */}
              {quoteResult && <motion.div ref={quoteResultRef} initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-5 pt-5 border-t border-stone-300/40 dark:border-white/10" data-id="element-790">
                  <p className="text-xs text-stone-700 dark:text-white/80 font-bold uppercase tracking-wider mb-3" data-id="element-791">
                    Price Breakdown
                  </p>
                  <p className="text-sm text-stone-800 dark:text-white/90 font-medium mb-4" data-id="element-792">
                    {quoteResult.quantity} yards of{' '}
                    <span className="font-bold" data-id="element-793">{quoteResult.material}</span>
                    {' → '}
                    {quoteResult.address}
                  </p>

                  {/* Line items */}
                  <div className="space-y-2 mb-4" data-id="element-794">
                    <div className="flex justify-between text-sm" data-id="element-795">
                      <span className="text-stone-700 dark:text-white/70" data-id="element-796">
                        Material — ${quoteResult.effectivePricePerYardLow}/yd ×{' '}
                        {quoteResult.quantity} yds
                      </span>
                      <span className="text-stone-900 dark:text-white font-semibold" data-id="element-797">
                        $
                        {quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}
                      </span>
                    </div>
                    {quoteResult.minLoadAdjustment && <div className="-mt-1 ml-0.5" data-id="element-798">
                        <button type="button" onClick={() => setShowMinLoadInfo(v => !v)} className="inline-flex items-center gap-1.5 text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={showMinLoadInfo} data-id="element-799">
                          <Info className="w-3.5 h-3.5" data-id="element-800" />
                          You asked for{' '}
                          {quoteResult.minLoadAdjustment.requested} yds — billed
                          for {quoteResult.minLoadAdjustment.billable} yds. Why?
                        </button>
                        <AnimatePresence data-id="element-801">
                          {showMinLoadInfo && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-802">
                              <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-803">
                                <p data-id="element-804">
                                  <span className="font-bold" data-id="element-805">
                                    5-yard minimum per delivery.
                                  </span>{' '}
                                  Your order splits into{' '}
                                  {quoteResult.deliveries} loads:
                                </p>
                                <ul className="list-disc list-inside space-y-0.5 text-stone-600 dark:text-white/70" data-id="element-806">
                                  {Array.from({
                          length: (quoteResult.deliveries || 1) - 1
                        }).map((_, i) => {
                          const isLight = isLightweight(quoteResult.material);
                          const full = isLight ? 15 : 12;
                          return <li key={i} data-id="element-807">
                                        Load {i + 1}: {full} yds (full)
                                      </li>;
                        })}
                                  <li data-id="element-808">
                                    Load {quoteResult.deliveries}:{' '}
                                    {quoteResult.minLoadAdjustment.lastLoadActual}{' '}
                                    yd needed → billed as 5 yds (minimum)
                                  </li>
                                </ul>
                                <p className="pt-1" data-id="element-809">
                                  Trucks have fixed dispatch costs, so each
                                  delivery is billed at 5 yds even if you need
                                  less. Round up your project to fill the truck
                                  and you'll get more material at no extra cost.
                                </p>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>}
                    {quoteResult.smallLoadSurcharge > 0 && <p className="text-xs text-[#1DB954] -mt-1 ml-0.5" data-id="element-810">
                        Includes small load upcharge ($
                        {quoteResult.basePricePerYardLow}/yd at 10+ yards)
                      </p>}
                    {quoteResult.bulkDiscount && quoteResult.bulkDiscount > 0 ? <p className="text-xs text-[#1DB954] -mt-1 ml-0.5" data-id="element-811">
                        {Math.round(quoteResult.bulkDiscount * 100)}% bulk
                        discount applied ({parseInt(quoteResult.quantity)}+
                        yards)
                      </p> : null}
                    <div className="flex justify-between text-sm" data-id="element-812">
                      <span className="text-stone-700 dark:text-white/70" data-id="element-813">
                        Delivery —{' '}
                        {quoteResult.deliveryZone !== null ? `Zone ${quoteResult.deliveryZone}` : 'Custom Quote'}
                        {quoteResult.deliveries && quoteResult.deliveries > 1 ? ` (×${quoteResult.deliveries})` : ''}
                      </span>
                      <span className="text-stone-900 dark:text-white font-semibold" data-id="element-814">
                        {quoteResult.deliveryZone !== null ? `$${quoteResult.deliveryFee}` : 'TBD'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-stone-300/40 dark:border-white/10" data-id="element-815">
                      <span className="text-stone-900 dark:text-white font-bold text-sm" data-id="element-816">
                        Estimated Total
                      </span>
                      <span className="text-2xl font-heading font-extrabold text-stone-900 dark:text-white" data-id="element-817">
                        ${quoteResult.priceLow}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4 flex justify-center" data-id="element-818">
                    <SameDayBadge variant="full" data-id="element-819" />
                  </div>

                  <div className="flex flex-col gap-3" data-id="element-820">
                    <button type="button" onClick={handleAddToTruckFromQuote} className="w-full bg-[#1DB954] hover:bg-[#17A347] text-white px-6 py-3.5 rounded-lg font-bold text-base transition-colors flex items-center justify-center shadow-[0_0_20px_rgba(29,185,84,0.25)]" data-id="element-821">
                      Add to Truck & Check Out
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-822" />
                    </button>
                    <div className={`grid ${quoteResult.materialSlug ? 'grid-cols-2' : 'grid-cols-1'} gap-2.5`} data-id="element-823">
                      <a href="tel:206-619-3742" className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#1DB954]/50 dark:hover:border-[#1DB954]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-824">
                        <Phone className="w-4 h-4 text-[#1DB954]" data-id="element-825" />
                        Call to Order
                      </a>
                      {quoteResult.materialSlug && <Link to={`/shop/${quoteResult.materialSlug}?qty=${encodeURIComponent(
                // Pass the user's REQUESTED yardage (e.g. 14), not
                // the billable value (17 after 5yd-min adjustment).
                // The Product page re-applies the split via
                // buildDeliveryLineItems so the cart shows the same
                // 12 + 5 breakdown the estimator quoted.
                String(quoteResult.minLoadAdjustment?.requested ?? quoteResult.quantity))}`} className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 hover:border-[#1DB954]/50 dark:hover:border-[#1DB954]/50 text-stone-800 dark:text-white text-sm font-semibold transition-all" data-id="element-826">
                          <FileText className="w-4 h-4 text-[#1DB954]" data-id="element-827" />
                          View Material
                        </Link>}
                    </div>
                  </div>

                  {/* Delivery Zone Note */}
                  {quoteResult.deliveryZone !== null ? <p className="mt-4 text-xs text-stone-700 dark:text-white/70 leading-relaxed text-center font-medium" data-id="element-828">
                      Delivery fee based on your{' '}
                      <Link to={`/delivery-zones?address=${encodeURIComponent(address)}`} className="text-[#1DB954] hover:underline" data-id="element-829">
                        delivery zone
                      </Link>
                      .
                    </p> : <div className="mt-3 flex items-center justify-center text-xs text-[#1DB954]" data-id="element-830">
                      <Info className="w-3.5 h-3.5 mr-1.5 shrink-0" data-id="element-831" />
                      Your address is outside standard zones. Delivery fee may
                      apply —{' '}
                      <Link to={`/delivery-zones?address=${encodeURIComponent(address)}`} className="font-semibold ml-1 hover:underline" data-id="element-832">
                        check delivery zones
                      </Link>
                    </div>}

                  {/* Discreet reset */}
                  <div className="mt-4 flex justify-end" data-id="element-833">
                    <button onClick={() => {
                handleCloseEstimator();
                setShowEstimator(true);
                setTimeout(() => {
                  if (estimatorRef.current) {
                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                    const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 80;
                    window.scrollTo({
                      top,
                      behavior: 'smooth'
                    });
                  }
                }, 150);
              }} className="text-[10px] text-stone-500 dark:text-white/40 hover:text-stone-800 dark:hover:text-white/70 transition-colors" data-id="element-834">
                      Start over ↻
                    </button>
                  </div>
                </motion.div>}
            </motion.div>}
        </AnimatePresence>

        {/* Catalog escape hatch — sits below the open estimator so the
            browse path stays discoverable without competing with the form. */}
        <AnimatePresence data-id="element-835">
          {showEstimator && <motion.div key="catalog-link" initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.25
        }} className="-mt-6 mb-8 flex justify-center md:justify-start max-w-3xl mx-auto md:mx-0" data-id="element-836">
              <motion.button type="button" onClick={() => {
            const el = document.getElementById('materials');
            if (el) {
              const navHeight = document.querySelector('nav')?.offsetHeight || 80;
              const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
              window.scrollTo({
                top,
                behavior: 'smooth'
              });
            }
          }} animate={{
            color: ['rgba(255,255,255,0.7)', 'rgba(255,255,255,1)', 'rgba(255,255,255,0.7)']
          }} transition={{
            duration: 2.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} whileHover={{
            color: '#1DB954'
          }} className="group inline-flex items-center text-sm font-medium" data-id="element-837">
                <span className="border-b border-current/40 group-hover:border-current pb-0.5" data-id="element-838">
                  Or browse the full catalog
                </span>
                <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-839" />
              </motion.button>
            </motion.div>}
        </AnimatePresence>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.4
      }} className="flex flex-wrap justify-center md:justify-start gap-x-3 gap-y-1 mt-8" data-id="element-840">
          <div className="hidden md:flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-841">
            <CheckCircle2 className="w-4 h-4 mr-2 text-[#1DB954]" data-id="element-842" />
            {t('hero.trust.fees')}
          </div>
          <div className="hidden md:flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-843">
            <CheckCircle2 className="w-4 h-4 mr-2 text-[#1DB954]" data-id="element-844" />
            {t('hero.trust.pay')}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.45
      }} className="flex justify-center md:justify-start mt-2" data-id="element-845">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-semibold bg-[#1DB954]/15 backdrop-blur-sm border border-[#1DB954]/50 text-white" data-id="element-846">
            <span data-id="element-847">🏠</span>
            30 Min from Your Driveway
          </span>
        </motion.div>

        <div className="mt-10" data-id="element-848">
          <LiveDeliveryTicker data-id="element-849" />
        </div>
      </div>
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-850" />
    </section>;
}